import { mapContactLensesToDraftLineItems } from "../../../../../utils/modules/EcommContent/ContactLensesEcommContent/utils/map-contact-lenses-to-draft-line-items.js";
import { isNoRxEntry, isRxEntryOptional, isRxEntryRequired } from "../../../../../types/modules/EcommContent/ContactLensesEcommContent/types/typeguards.js";
import { isWithoutRx, isWithRx } from "../../../../../../components/RxableSelect/modules/EcommContent/components/RxableSelect/is-rx-radio-selection.js";
import { isRecoverableError, isNonRecoverableError, isSubscriptionPurchase, isNonExecutableQueryError } from "../../../../../../../../types/types/typeguards.js";
import { CartDiscountType } from "../../../../../../../../hooks/useCartDiscountsQuery/hooks/useCartDiscountsQuery/types.js";
const columnsIsNonOrderable = (column) => {
  return column.isEnabled && column.quantitySelector.maxSelectableQuantity === 0;
};
const getConfiguratorType = (state) => {
  const { rxEntryType } = state.configurator;
  const { rxToggle } = state.configurations;
  if (isNoRxEntry(rxEntryType) || isRxEntryOptional(rxEntryType) && isWithoutRx(rxToggle)) {
    return "one-column";
  }
  if (isRxEntryRequired(rxEntryType) || isRxEntryOptional(rxEntryType) && isWithRx(rxToggle)) {
    return "two-column";
  }
  return "two-column";
};
const hasNullValues = (config) => Object.values(config).some((value) => value === null);
const isColumnValid = (columnState, columnConfigurations) => {
  if (!columnState.isEnabled) {
    return true;
  }
  if (!hasNullValues(columnConfigurations.prescription)) {
    return true;
  }
  return false;
};
const isRxColumnValid = (state) => {
  return {
    left: isColumnValid(state.configurator.left, state.configurations.left),
    right: isColumnValid(state.configurator.right, state.configurations.right),
    both: isColumnValid(state.configurator.both, state.configurations.both)
  };
};
const areColumnStatesValid = (state) => {
  const rxColumnValidations = isRxColumnValid(state);
  if (isOneColConfigurator(state)) {
    return rxColumnValidations.both;
  }
  return rxColumnValidations.left && rxColumnValidations.right;
};
const areColumnCheckboxStatesValid = (state) => {
  if (isOneColConfigurator(state)) {
    return true;
  }
  return state.configurator.left.isEnabled || state.configurator.right.isEnabled;
};
const isDeliveryIntervalStateValid = ({
  configurations
}) => {
  const { purchaseType, subscriptionPurchaseConfigurations } = configurations;
  if (isSubscriptionPurchase(purchaseType)) {
    return subscriptionPurchaseConfigurations.deliveryInterval !== null;
  }
  return void 0;
};
const isConfiguratorValid = (state) => {
  const areColumnsValid = areColumnStatesValid(state);
  const areColumnCheckboxesValid = areColumnCheckboxStatesValid(state);
  const isDeliveryIntervalValid = isDeliveryIntervalStateValid(state);
  const hasInvalid = [areColumnCheckboxesValid, areColumnsValid, isDeliveryIntervalValid].some(
    (value) => value === false
  );
  return [!hasInvalid, { areColumnsValid, areColumnCheckboxesValid, isDeliveryIntervalValid }];
};
const isOneColConfigurator = (state) => {
  return getConfiguratorType(state) === "one-column";
};
const hasNonOrderableProduct = (state) => {
  if (isOneColConfigurator(state)) {
    return columnsIsNonOrderable(state.configurator.both);
  }
  return columnsIsNonOrderable(state.configurator.left) || columnsIsNonOrderable(state.configurator.right);
};
const hasNonRecoverableErrorOnEyeside = (state, eyeSide) => {
  const { configurator, queries } = state;
  return configurator[eyeSide].isEnabled && isNonRecoverableError(queries[eyeSide].error);
};
const hasNonRecoverableError = (state) => {
  if (isOneColConfigurator(state)) {
    return hasNonRecoverableErrorOnEyeside(state, "both");
  }
  return hasNonRecoverableErrorOnEyeside(state, "left") || hasNonRecoverableErrorOnEyeside(state, "right");
};
const hasRecoverableErrorOnEyeside = (state, eyeSide) => {
  const { configurator, queries } = state;
  return configurator[eyeSide].isEnabled && isRecoverableError(queries[eyeSide].error);
};
const hasRecoverableError = (state) => {
  if (isOneColConfigurator(state)) {
    return hasRecoverableErrorOnEyeside(state, "both");
  }
  return hasRecoverableErrorOnEyeside(state, "left") || hasRecoverableErrorOnEyeside(state, "right");
};
const hasNonExecutableErrorOnEyeside = (state, eyeSide) => {
  const { configurator, queries } = state;
  return configurator[eyeSide].isEnabled && isNonExecutableQueryError(queries[eyeSide].error);
};
const hasNonExecutableQueryError = (state) => {
  if (isOneColConfigurator(state)) {
    return hasNonExecutableErrorOnEyeside(state, "both");
  }
  return hasNonExecutableErrorOnEyeside(state, "left") || hasNonExecutableErrorOnEyeside(state, "right");
};
const hasQueryError = (state) => {
  return hasNonRecoverableError(state) || hasRecoverableError(state);
};
const hasQueryLoading = (state) => {
  if (isOneColConfigurator(state)) {
    return state.queries.both.loading;
  }
  return state.queries.left.loading || state.queries.right.loading;
};
const getEnabledSelectedQuantity = (state) => {
  const { configurator, configurations } = state;
  if (isOneColConfigurator(state)) {
    return configurations.both.quantity;
  }
  if (!areColumnCheckboxStatesValid(state)) {
    return null;
  }
  const {
    left: { quantity: leftQuantity },
    right: { quantity: rightQuantity }
  } = configurations;
  const { left: leftColumn, right: rightColumn } = configurator;
  if (!leftColumn.isEnabled) {
    return rightQuantity;
  }
  if (!rightColumn.isEnabled) {
    return leftQuantity;
  }
  return leftQuantity + rightQuantity;
};
const getLineItemDrafts = (state, priceSegmentChannelKey) => {
  if (hasQueryError(state)) {
    return [];
  }
  return mapContactLensesToDraftLineItems(
    state,
    isOneColConfigurator(state),
    priceSegmentChannelKey
  );
};
const getCartDiscountUnitPrice = (unitPrice, cartDiscount) => {
  if (!cartDiscount) {
    return 0;
  }
  const { type, amount } = cartDiscount;
  if (type === CartDiscountType.PERCENT) {
    const discount = unitPrice * amount / 100;
    return unitPrice - discount;
  }
  return unitPrice - amount;
};
const getPricedQuantities = (totalQuantity, cartDiscount) => {
  if (!cartDiscount) {
    return {
      regularPricedQuantity: totalQuantity,
      cartDiscountedPricedQuantity: 0
    };
  }
  const regularPricedQuantity = totalQuantity % cartDiscount.minimumQuantity;
  const cartDiscountedPricedQuantity = totalQuantity - regularPricedQuantity;
  return {
    regularPricedQuantity,
    cartDiscountedPricedQuantity
  };
};
const getTotalPurchasePrice = (state, displayPrice, cartDiscount) => {
  const totalQuantity = getEnabledSelectedQuantity(state);
  if (!totalQuantity) {
    return void 0;
  }
  const unitPrice = displayPrice.discountedPrice ?? displayPrice.originalPrice;
  if (cartDiscount) {
    const cartDiscountUnitPrice = getCartDiscountUnitPrice(unitPrice, cartDiscount?.discount);
    const { regularPricedQuantity, cartDiscountedPricedQuantity } = getPricedQuantities(
      totalQuantity,
      cartDiscount
    );
    return regularPricedQuantity * unitPrice + cartDiscountedPricedQuantity * cartDiscountUnitPrice;
  }
  return totalQuantity * unitPrice;
};
const hasSufficientStock = (available, quantity) => {
  if (quantity === void 0 || available === void 0) return void 0;
  return available >= quantity;
};
const getSelectedPrescriptionStockStatus = (state) => {
  const { configurations, configurator } = state;
  const {
    left: { quantity: leftQuantity },
    right: { quantity: rightQuantity },
    both: { quantity: bothQuantity }
  } = configurations;
  const {
    left: { availableQuantity: leftAvailableQuantity },
    right: { availableQuantity: rightAvailableQuantity },
    both: { availableQuantity: bothAvailableQuantity }
  } = configurator;
  if (isOneColConfigurator(state)) {
    if (!bothQuantity) {
      return void 0;
    }
    const bothEyeStock = hasSufficientStock(bothAvailableQuantity, bothQuantity);
    if (bothEyeStock === void 0) {
      return void 0;
    }
    return bothEyeStock ? "in-stock" : "out-of-stock";
  }
  if (!leftQuantity && !rightQuantity) {
    return void 0;
  }
  const leftEyeStock = hasSufficientStock(leftAvailableQuantity, leftQuantity);
  const rightEyeStock = hasSufficientStock(rightAvailableQuantity, rightQuantity);
  if (leftEyeStock === false || rightEyeStock === false) {
    return "out-of-stock";
  }
  return "in-stock";
};
const getSelectors = () => {
  return {
    hasNonOrderableProduct,
    hasNonRecoverableError,
    hasNonRecoverableErrorOnEyeside,
    hasNonExecutableQueryError,
    hasRecoverableErrorOnEyeside,
    hasRecoverableError,
    hasQueryError,
    hasQueryLoading,
    isRxColumnValid,
    isConfiguratorValid,
    isOneColConfigurator,
    getConfiguratorType,
    getEnabledSelectedQuantity,
    getLineItemDrafts,
    getTotalPurchasePrice,
    getSelectedPrescriptionStockStatus
  };
};
export {
  areColumnCheckboxStatesValid,
  areColumnStatesValid,
  getSelectors,
  isDeliveryIntervalStateValid
};
//# sourceMappingURL=selectors.js.map
